import {
  CallDto,
  FilterGroupDto,
  NeedDto,
  SuggestedActionDto,
  SuggestedScheduledEventDto,
  VoiceCallAuditDto,
} from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { callsRequests } from '../requests/calls.request';
import { useAppSelector } from '../store-hooks';
import { CallState } from '../types/callsTypes';
import { createApiThunk } from '../utils/api-thunk';
import { ActivitySelectorParams } from '../types/selector-types';
import { getGuideInteractions } from './docketSlice';
import { feedSlice, getFeedItems } from './feedSlice';
import { resetRedux } from '../actions/shared-actions';
import { CallUpdatedTypes } from '@act/shared/socket-events';
import { addToEffectsMiddleware } from '../utils/effects-middleware';
import type { WebsocketEventActions } from './websocketSlice';
// Import shared thunk to avoid circular dependency
import { getCalls } from '../thunks/calls.thunks';
export { getCalls };

// INITIAL STATE ===============================================================
const callsAdapter = createEntityAdapter<CallDto>();
const voiceCallReviewAdapter = createEntityAdapter<VoiceCallAuditDto>();

const initialState: CallState = {
  callPageIds: [],
  callPageTotalCount: 0,
  calls: callsAdapter.getInitialState({
    status: 'initial',
  }),
  voiceCallReviews: voiceCallReviewAdapter.getInitialState({
    status: 'initial',
  }),
  callFilterOptions: null,
  callFilterOptionStatus: 'initial',
  suggestions: {},
  suggestionsLoadingStatus: {},
};

// THUNKS/ACTIONS ==============================================================
export const updateCallNoFollowUpNeeded = createApiThunk(
  callsRequests.updateCallNoFollowUpNeeded
);
export const getCallFilters = createApiThunk(callsRequests.getCallsFilters);
export const updateSubjectId = createApiThunk(callsRequests.updateSubjectId);

export const getCallReviewTasks = createApiThunk(
  callsRequests.getCallReviewTask
);
export const updateCallReview = createApiThunk(callsRequests.updateCallReview);

export const getSuggestions = createApiThunk(callsRequests.getSuggestions);
export const updateSubjectIdSuccess = createAction<CallDto>(
  'calls/updateSubjectIdSuccess'
);

export const suggestedNeedUpdated = createAction<{
  callId: number;
  tempNeedId: string;
  createdNeed: NeedDto;
}>('calls/suggestedNeedUpdated');

export const suggestedActionUpdated = createAction<SuggestedActionDto>(
  'calls/suggestedActionUpdated'
);

export const suggestedScheduledEventUpdated =
  createAction<SuggestedScheduledEventDto>(
    'calls/suggestedScheduledEventUpdated'
  );
// SLICE =======================================================================
export const callsSlice = createSlice({
  name: 'calls',
  initialState,
  reducers: {
    callUpdated: (
      state,
      action: PayloadAction<{ call: CallDto; originalCall: CallDto }>
    ) => {
      const { call } = action.payload;
      if (state.calls.entities[call.id]) {
        // Update the call with the new data
        state.calls.entities[call.id] = {
          ...state.calls.entities[call.id],
          ...call,
        };
      }
    },
    suggestedActionsUpdated: (
      state,
      action: PayloadAction<{ call: CallDto; originalCall: CallDto }>
    ) => {
      const callId = action.payload.call.id;
      if (state.suggestions[callId]) {
        delete state.suggestions[callId];
      }
      state.suggestionsLoadingStatus[callId] = 'loading';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getCalls.pending, state => {
        state.calls.status = 'loading';
      })
      .addCase(getCalls.fulfilled, (state, action) => {
        // Remove only the previously paginated calls to prevent store pollution
        // This preserves feed calls and other calls loaded from different sources
        if (state.callPageIds.length > 0) {
          callsAdapter.removeMany(state.calls, state.callPageIds);
        }

        // Add only the new filtered results
        callsAdapter.addMany(state.calls, action.payload.calls);
        state.callPageTotalCount = action.payload.count;
        state.callPageIds = action.payload.calls.map(call => call.id);
        state.calls.status = 'success';
      })
      .addCase(getCalls.rejected, state => {
        state.calls.status = 'failed';
      })
      .addCase(getFeedItems.fulfilled, (state, action) => {
        callsAdapter.addMany(state.calls, action.payload.calls);
        state.calls.status = 'success';
      })
      .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
        if (!action.payload.feedItemModelView.calls) return;

        callsAdapter.upsertMany(
          state.calls,
          action.payload.feedItemModelView.calls
        );
      })
      // Docket Interactions
      .addCase(getGuideInteractions.fulfilled, (state, action) => {
        callsAdapter.addMany(state.calls, action.payload.calls);
      })
      .addCase(getCallFilters.pending, state => {
        state.callFilterOptionStatus = 'loading';
      })
      .addCase(
        getCallFilters.fulfilled,
        (state, action: PayloadAction<FilterGroupDto>) => {
          state.callFilterOptionStatus = 'success';
          state.callFilterOptions = action.payload;
        }
      )
      .addCase(getCallFilters.rejected, state => {
        state.callFilterOptionStatus = 'failed';
      })
      .addCase(updateSubjectId.pending, state => {
        state.calls.status = 'loading';
      })
      .addCase(
        updateSubjectId.fulfilled,
        (state, action: PayloadAction<CallDto>) => {
          state.calls.status = 'success';
          state.calls.entities[action.payload.id].subjectId =
            action.payload.subjectId;
        }
      )
      .addCase(updateSubjectIdSuccess, (state, action) => {
        if (state.calls.entities[action.payload.id]) {
          state.calls.entities[action.payload.id] = {
            ...state.calls.entities[action.payload.id],
            ...action.payload,
          };
        }
        state.calls.status = 'success';
      })
      .addCase(updateSubjectId.rejected, state => {
        state.calls.status = 'failed';
      })
      .addCase(getCallReviewTasks.pending, state => {
        state.voiceCallReviews.status = 'loading';
      })
      .addCase(getCallReviewTasks.fulfilled, (state, action) => {
        voiceCallReviewAdapter.addMany(state.voiceCallReviews, action.payload);
        state.voiceCallReviews.status = 'success';
      })
      .addCase(getCallReviewTasks.rejected, state => {
        state.voiceCallReviews.status = 'failed';
      })
      .addCase(updateCallReview.fulfilled, (state, action) => {
        voiceCallReviewAdapter.upsertOne(
          state.voiceCallReviews,
          action.payload
        );
      })
      .addCase(getSuggestions.pending, (state, action) => {
        const callId = action.meta.arg?.params?.id;
        if (callId) {
          state.suggestionsLoadingStatus[callId] = 'loading';
        }
      })
      .addCase(getSuggestions.fulfilled, (state, action) => {
        const callId = action.meta.arg?.params?.id;
        if (callId) {
          const filteredPayload = {
            ...action.payload,
            actions: action.payload?.actions?.filter(
              action => !(action?.tempNeedId && !action?.needId)
            ),
          };
          state.suggestions[callId] = filteredPayload;
          state.suggestionsLoadingStatus[callId] = 'success';
        }
      })
      .addCase(getSuggestions.rejected, (state, action) => {
        const callId = action.meta.arg?.params?.id;
        if (callId) {
          state.suggestionsLoadingStatus[callId] = 'failed';
        }
      });
  },
});

// SELECTORS ===================================================================
export const selectCallSliceState = state =>
  (state as { calls: typeof initialState })?.calls;

const selectCallsState = createSelector(selectCallSliceState, s => s.calls);

export const { selectAll: selectCalls, selectEntities: selectCallsMap } =
  callsAdapter.getSelectors(selectCallsState);

export const selectCallFilters = createSelector(
  selectCallSliceState,
  s => s.callFilterOptions
);

export const selectCallFilterLoadingStatus = createSelector(
  selectCallSliceState,
  s => s.callFilterOptionStatus
);

const { selectById } = callsAdapter.getSelectors();

const {
  selectById: selectVoiceCallAuditById,
  selectAll: selectVoiceCallReviews,
} = voiceCallReviewAdapter.getSelectors();

const selectCallsAreLoading = state => selectCallSliceState(state).calls.status;
const selectCallsReviewsAreLoading = state =>
  selectCallSliceState(state).voiceCallReviews.status;

const selectCallsPage = createSelector(selectCallSliceState, state =>
  state.callPageIds.map(id => state.calls.entities[id])
);

export const selectCallsPageTotal = state =>
  (state as { calls: typeof initialState })?.calls.callPageTotalCount;

export const selectCallsByGuideAndTime: (
  state,
  paramObj: ActivitySelectorParams
) => CallDto[] = createSelector(
  [
    selectCalls,
    (state, paramObj: ActivitySelectorParams) => paramObj.guideId,
    (state, paramObj: ActivitySelectorParams) => paramObj.startEpochTime,
    (state, paramObj: ActivitySelectorParams) => paramObj.endEpochTime,
  ],
  (calls, guideId, startEpochTime, endEpochTime) => {
    return calls.filter(
      c =>
        (c.originator?.userId === guideId || c.receiver?.userId === guideId) &&
        c.timestamp >= startEpochTime &&
        c.timestamp <= endEpochTime
    );
  }
);

// Voice Call Audit Selectors
const selectvoiceCallReviewsState = createSelector(
  selectCallSliceState,
  s => s.voiceCallReviews
);

export const selectVoiceCallReviewsByCallId = createSelector(
  [selectvoiceCallReviewsState, (state, callId: number) => callId],
  (auditsState, callId) => {
    const audits = Object.values(auditsState.entities || {});
    const filtered = audits.filter(
      audit => audit && Number(audit.callId) === callId
    );
    return filtered;
  }
);

export const selectVoiceCallReviewsByAssigneeId = createSelector(
  [selectvoiceCallReviewsState, (state, assigneeId: string) => assigneeId],
  (auditsState, assigneeId) => {
    const audits = Object.values(auditsState.entities || {});
    return audits.filter(audit => audit && audit.assigneeId === assigneeId);
  }
);

// Suggestions Selectors
export const selectSuggestionsByCallId = createSelector(
  [selectCallSliceState, (state, callId: number) => callId],
  (callState, callId) => callState.suggestions[callId] || null
);

export const selectSuggestionsLoadingStatusByCallId = createSelector(
  [selectCallSliceState, (state, callId: number) => callId],
  (callState, callId) => callState.suggestionsLoadingStatus[callId] || 'initial'
);

// REDUCER =====================================================================
export const callsReducer = callsSlice.reducer;

// EXPORT HELPER ===============================================================
export const callsAppSelectors = {
  useSelectCallById: id =>
    useAppSelector(state => selectById(state.calls.calls, id)),
  useSelectAllCalls: () => useAppSelector(selectCalls),
  useSelectCallsPage: () => useAppSelector(selectCallsPage),
  useSelectCallsPageTotal: () => useAppSelector(selectCallsPageTotal),
  useSelectCallsFilters: () => useAppSelector(selectCallFilters),
  useSelectCallsFiltersLoadingStatus: () =>
    useAppSelector(selectCallFilterLoadingStatus),
  useSelectCallsAreLoading: () => useAppSelector(selectCallsAreLoading),
  useSelectCallsReviewsAreLoading: () =>
    useAppSelector(selectCallsReviewsAreLoading),
  useSelectVoiceCallReviewsByCallId: (callId: number) =>
    useAppSelector(state => selectVoiceCallReviewsByCallId(state, callId)),
  useSelectVoiceCallReviewsByAssigneeId: (assigneeId: string) =>
    useAppSelector(state =>
      selectVoiceCallReviewsByAssigneeId(state, assigneeId)
    ),
  useSelectVoiceCallAuditById: (id: number) =>
    useAppSelector(state =>
      selectVoiceCallAuditById(state.calls.voiceCallReviews, id)
    ),
  useSelectAllCallReviews: () =>
    useAppSelector(state =>
      selectVoiceCallReviews(selectvoiceCallReviewsState(state))
    ),
  useSelectSuggestionsByCallId: (callId: number) =>
    useAppSelector(state => selectSuggestionsByCallId(state, callId)),
  useSelectSuggestionsLoadingStatusByCallId: (callId: number) =>
    useAppSelector(state =>
      selectSuggestionsLoadingStatusByCallId(state, callId)
    ),
};

// WEBSOCKET ACTIONS ===========================================================
export const callsSliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: CallUpdatedTypes.CALL_UPDATED,
    action: callsSlice.actions.callUpdated,
    channel: 'baseSocket',
  },
  {
    type: CallUpdatedTypes.SUGGESTED_ACTIONS_UPDATED,
    action: callsSlice.actions.suggestedActionsUpdated,
    channel: 'baseSocket',
  },
];

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: callsSlice.actions.suggestedActionsUpdated,
    effect: async ({ dispatch, action }) => {
      const { call } = action.payload;
      dispatch(getSuggestions({ params: { id: call.id } }));
    },
  });
});
